// src/pages/LoginPage.js
//
// Ticket 02: real email+password auth with one-shot legacy migration.
//
// Flow:
//   1. User types email + password.
//   2. Email is still gated on configuration/main.users[email] in this
//      ticket — ticket 04 replaces the gate with a memberships check.
//   3. We try signInWithEmailAndPassword(email, enteredPassword).
//   4. On auth/wrong-password: call tryLegacyPasswordMigration(). If the
//      user was still holding the original DUMMY_PASSWORD, it gets swapped
//      to whatever they just typed and users/{uid}.legacyPasswordMigrated
//      becomes true.
//   5. On auth/user-not-found: create the account with the typed password
//      directly. This is the path for admins pre-provisioning a user in
//      config — the first person to land on their email gets to choose
//      the password. Mark them as already migrated.
//
// "Forgot password?" link uses Firebase's built-in password reset flow.

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
} from 'firebase/auth';
import { Eye, EyeOff } from 'lucide-react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import LoginLoader from '../components/LoginLoader';
import InitialLoader from '../components/InitialLoader';
import AuthDataNotice from '../components/AuthDataNotice';
import { ensureUserDoc, tryLegacyPasswordMigration } from '../auth/userHelpers';
import { getMembership } from '../auth/membershipHelpers';
import { LEGACY_DEFAULT_ORG_ID } from '../hooks/useActiveOrg';
import { listPendingInvitesForEmail } from '../data/invitesHelpers';

const USING_FIREBASE_EMULATORS =
  process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true';

async function fetchLatestEmulatorResetLink(userEmail) {
  if (!USING_FIREBASE_EMULATORS) return null;
  const projectId = auth.app.options.projectId;
  if (!projectId) return null;

  const response = await fetch(
    `http://127.0.0.1:9099/emulator/v1/projects/${projectId}/oobCodes`,
  );
  if (!response.ok) {
    throw new Error(`Could not read Auth emulator OOB codes (${response.status}).`);
  }

  const payload = await response.json();
  const entries = Array.isArray(payload?.oobCodes) ? payload.oobCodes : [];
  const normalized = (userEmail || '').toLowerCase().trim();
  const latest = [...entries]
    .reverse()
    .find(
      (entry) =>
        entry?.requestType === 'PASSWORD_RESET' &&
        (entry?.email || '').toLowerCase() === normalized,
    );

  return latest?.oobLink || null;
}

function getOobCodeFromLink(resetLink) {
  if (!resetLink) return null;
  try {
    return new URL(resetLink).searchParams.get('oobCode');
  } catch (err) {
    return null;
  }
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [devResetLink, setDevResetLink] = useState('');
  const [devResetPassword, setDevResetPassword] = useState('');
  const [devResetStatus, setDevResetStatus] = useState('');
  const [devResetLoading, setDevResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showDevResetPassword, setShowDevResetPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestedEmails, setSuggestedEmails] = useState([]);

  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    // Ticket 10: configuration/main is deprecated and will be denied by
    // the strict rules in ticket 11. LoginPage no longer reads it; the
    // gate runs purely against memberships/{uid}_{orgId}.
    const storedEmails = JSON.parse(
      localStorage.getItem('researcherEmails') || '[]',
    );
    setSuggestedEmails(storedEmails);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    setProgress(10);
    setLoadingText('Verifying your credentials...');

    await new Promise((res) => setTimeout(res, 200));
    const userEmail = email.trim().toLowerCase();

    // Ticket 04: we no longer gate on configuration/main.users[email]
    // before authentication. Anyone can TRY to sign in. The gate moves
    // below the auth call and runs against memberships/{uid} with a
    // self-heal fallback for legacy users the migration script skipped.
    setProgress(30);
    setLoadingText('Authenticating...');

    let credential = null;
    try {
      credential = await signInWithEmailAndPassword(auth, userEmail, password);
    } catch (authError) {
      if (authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
        // Possible legacy user still holding the old DUMMY_PASSWORD.
        // tryLegacyPasswordMigration() returns a live credential on success
        // or null if the dummy fallback didn't apply.
        try {
          setProgress(45);
          setLoadingText('Upgrading legacy account...');
          credential = await tryLegacyPasswordMigration(userEmail, password);
        } catch (migrationErr) {
          setError(migrationErr.message || 'Password migration failed.');
          setLoading(false);
          return;
        }
        if (!credential) {
          setError('Incorrect password. Use "Forgot password?" to reset.');
          setLoading(false);
          return;
        }
      } else if (authError.code === 'auth/user-not-found') {
        // Ticket 10: no more configuration/main.users self-heal. Unknown
        // emails are rejected outright — users reach the app via the
        // self-serve signup flow (ticket 14) or an admin invite
        // (ticket 15), not by typing an arbitrary email into login.
        setError(
          'No account with that email. Use "Create an account" to sign up.',
        );
        setLoading(false);
        return;
      } else {
        setError(`Authentication error: ${authError.message}`);
        setLoading(false);
        return;
      }
    }

    setProgress(80);
    setLoadingText('Finalizing session...');

    // Ticket 15: super admins bypass the org membership gate. The
    // platform role is independent of org membership — a super admin
    // can exist without being in any org, so we check users/{uid}.
    // globalRole first and short-circuit into /super-admin.
    try {
      const userSnap = await getDoc(doc(db, 'users', credential.user.uid));
      if (userSnap.exists() && userSnap.data().globalRole === 'superAdmin') {
        await ensureUserDoc(credential.user.uid, {
          email: userEmail,
          overrides: {},
        });
        setProgress(100);
        navigate('/super-admin');
        return;
      }
    } catch (e) {
      // If the read fails, fall through to the membership gate.
    }

    // Ticket 12 gate: any approved membership is enough. We query
    // memberships by uid and let SessionProvider pick the active org
    // on the next render. The query is permissive under the rules
    // (list is signed-in-only; caller can only see their own uid
    // anyway). The legacy-default-org direct read stays as a fast
    // path for the default case.
    // Legacy fast path: a direct get on memberships/{uid}_{legacyOrgId}.
    // Post-ticket-10 most users don't have this doc, so the get hits
    // the rules engine on a non-existent resource. With the ticket-11
    // rules in place that surfaced as a "Null value error" pageerror;
    // we've since added a `resource != null` guard, but we still catch
    // any permission error here so a rules change can't crash the
    // login path in the future.
    const legacyId = `${credential.user.uid}_${LEGACY_DEFAULT_ORG_ID}`;
    let membership = null;
    try {
      membership = await getMembership(legacyId);
    } catch (probeErr) {
      // Expected deny when the doc doesn't exist — fall through to
      // the uid-wide query below.
    }

    if (!membership) {
      // Fallback: any membership for this uid. Matches the session
      // provider's logic.
      const q = query(
        collection(db, 'memberships'),
        where('uid', '==', credential.user.uid),
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        membership = { id: snap.docs[0].id, ...snap.docs[0].data() };
      }
    }

    if (!membership) {
      const pendingInvites = await listPendingInvitesForEmail(userEmail);
      if (pendingInvites.length > 0) {
        await ensureUserDoc(credential.user.uid, {
          email: userEmail,
          overrides: {},
        });

        const emailSet = new Set(
          JSON.parse(localStorage.getItem('researcherEmails') || '[]'),
        );
        emailSet.add(userEmail);
        localStorage.setItem('researcherEmails', JSON.stringify(Array.from(emailSet)));

        setProgress(100);
        setLoadingText('Redirecting to your invite...');
        await new Promise((res) => setTimeout(res, 300));
        navigate('/');
        return;
      }

      setError('This email address is not authorized to access the platform.');
      setLoading(false);
      return;
    }

    // users/{uid} upsert keeps the user's lastLogin fresh. Role now
    // lives on memberships; the legacy users/{uid}.role field is dead.
    await ensureUserDoc(credential.user.uid, {
      email: userEmail,
      overrides: {},
    });

    // Cache email for autocomplete on next login.
    const emailSet = new Set(
      JSON.parse(localStorage.getItem('researcherEmails') || '[]'),
    );
    emailSet.add(userEmail);
    localStorage.setItem('researcherEmails', JSON.stringify(Array.from(emailSet)));

    setProgress(100);
    setLoadingText('Redirecting to your workspace...');
    await new Promise((res) => setTimeout(res, 300));
    navigate('/');
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfo('');
    setDevResetLink('');
    setDevResetPassword('');
    setDevResetStatus('');
    const userEmail = email.trim().toLowerCase();
    if (!userEmail) {
      setError('Enter your email above first, then click "Forgot password?".');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, userEmail);
      setInfo(`Password reset email sent to ${userEmail}. Check your inbox.`);
      if (USING_FIREBASE_EMULATORS) {
        const resetLink = await fetchLatestEmulatorResetLink(userEmail);
        if (resetLink) {
          setDevResetLink(resetLink);
        }
      }
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        // Don't leak whether an email has an account. Show the same success
        // message as a valid email would produce.
        setInfo(`If an account exists for ${userEmail}, a reset email is on its way.`);
        if (USING_FIREBASE_EMULATORS) {
          const resetLink = await fetchLatestEmulatorResetLink(userEmail).catch(
            () => null,
          );
          if (resetLink) {
            setDevResetLink(resetLink);
          }
        }
      } else {
        setError(`Could not send reset email: ${err.message}`);
      }
    }
  };

  const handleDevResetPassword = async () => {
    setError('');
    setDevResetStatus('');
    const oobCode = getOobCodeFromLink(devResetLink);
    if (!oobCode) {
      setError('Could not read the local reset code. Request a new reset link.');
      return;
    }
    if (devResetPassword.trim().length < 8) {
      setError('Enter a new password with at least 8 characters.');
      return;
    }

    setDevResetLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, devResetPassword);
      setPassword(devResetPassword);
      setDevResetStatus('Local password reset complete. You can sign in now.');
      setInfo('Password reset completed locally.');
      setDevResetLink('');
    } catch (err) {
      setError(`Could not complete the local reset: ${err.message}`);
    } finally {
      setDevResetLoading(false);
    }
  };

  // Ticket 10: no more config doc to wait for. The loader briefly flashes
  // while React mounts, then the form renders.
  // eslint-disable-next-line no-unused-vars
  const _showInitialLoader = false;
  if (_showInitialLoader) {
    return <InitialLoader />;
  }

  const renderPasswordField = ({
    id,
    value,
    setValue,
    shown,
    setShown,
    required = true,
    autoComplete,
    minLength,
    placeholder,
    className = '',
  }) => (
    <div className="relative">
      <input
        id={id}
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        minLength={minLength}
        className={`w-full p-3 pr-12 border border-slate-300 rounded-lg focus-academic transition-colors duration-200 ${className}`.trim()}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setShown((prev) => !prev)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700"
      >
        {shown ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );

  return (
    <>
      {loading && <LoginLoader progress={progress} text={loadingText} />}

      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl academic-shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">📚</div>
            <h1 className="text-slate-900 text-3xl font-bold mb-2">
              Research Platform Login
            </h1>
            <p className="text-slate-600">
              Sign in with your research email and password.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="li-email" className="block text-slate-700 font-semibold mb-2">
                Email address
              </label>
              <input
                id="li-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="researcher@institution.edu"
                className="w-full p-3 border border-slate-300 rounded-lg focus-academic transition-colors duration-200"
                list="suggested-emails"
              />
              <datalist id="suggested-emails">
                {suggestedEmails.map((e) => (
                  <option key={e} value={e} />
                ))}
              </datalist>
            </div>

            <div>
              <label htmlFor="li-pass" className="block text-slate-700 font-semibold mb-2">
                Password
              </label>
              {renderPasswordField({
                id: 'li-pass',
                value: password,
                setValue: setPassword,
                shown: showPassword,
                setShown: setShowPassword,
                autoComplete: 'current-password',
                minLength: 8,
                placeholder: 'Your password',
              })}
              <button
                type="button"
                onClick={handleForgotPassword}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-center text-sm">
                {error}
              </div>
            )}
            {info && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 p-3 rounded-lg text-center text-sm">
                {info}
              </div>
            )}
            {USING_FIREBASE_EMULATORS && devResetLink && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded-lg text-sm">
                <p className="font-semibold mb-1">Local password reset</p>
                <p className="mb-2">
                  Firebase Auth is running in emulator mode, so no real email was
                  sent. Set the new password here instead.
                </p>
                {renderPasswordField({
                  id: 'dev-reset-pass',
                  value: devResetPassword,
                  setValue: setDevResetPassword,
                  shown: showDevResetPassword,
                  setShown: setShowDevResetPassword,
                  required: false,
                  autoComplete: 'new-password',
                  minLength: 8,
                  placeholder: 'New password',
                  className: 'border-amber-300 bg-white mb-2',
                })}
                <button
                  type="button"
                  onClick={handleDevResetPassword}
                  disabled={devResetLoading}
                  className="w-full p-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors duration-200 font-semibold disabled:bg-amber-300 disabled:cursor-not-allowed"
                >
                  {devResetLoading ? 'Resetting...' : 'Reset Password Locally'}
                </button>
                {devResetStatus && (
                  <p className="mt-2 text-green-800">{devResetStatus}</p>
                )}
                <details className="mt-3">
                  <summary className="cursor-pointer text-slate-700">
                    Show raw emulator link
                  </summary>
                  <a
                    href={devResetLink}
                    target="_blank"
                    rel="noreferrer"
                    className="block mt-2 text-blue-700 underline break-all"
                  >
                    {devResetLink}
                  </a>
                </details>
              </div>
            )}

            <AuthDataNotice variant="dark" />

            <button
              type="submit"
              disabled={loading}
              className="w-full p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              Sign in
            </button>

            <p className="text-center text-sm text-slate-600">
              New to the platform?{' '}
              <Link to="/signup" className="text-blue-600 hover:underline font-semibold">
                Create an account
              </Link>
            </p>
          </form>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
